import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { ClientLayout } from '../../layouts/ClientLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useClientOnboarding } from '../../hooks/useClientOnboarding';
import WelcomeModal from '../WelcomeModal';
import ProfilePage from './ProfilePage';
import { TileGrid } from './TileGrid';
import GoalManagement from '../shared/GoalManagement';
import { ClientIntegrations } from './ClientIntegrations';

/**
 * ClientDashboardLegacy - Preserved legacy client dashboard
 *
 * This is the original client dashboard implementation, preserved for:
 * - Reference during new view development
 * - Fallback if issues found in new views
 * - Gradual user transition
 *
 * Access via: /client/legacy/*
 * Switch to new view via banner at top
 */
const ClientDashboardLegacy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Extract current view from URL path - handle both legacy and non-legacy routes
  const pathSegments = location.pathname.split('/').filter(Boolean);
  // Possible paths:
  // /client/legacy/goals -> ['client', 'legacy', 'goals'] -> 'goals'
  // /client/legacy -> ['client', 'legacy'] -> 'home'
  // /client/goals -> ['client', 'goals'] -> 'goals'
  // /client -> ['client'] -> 'home'
  const legacyIndex = pathSegments.indexOf('legacy');
  let currentView: string;
  if (legacyIndex !== -1) {
    // Legacy path: /client/legacy/[view]
    currentView = pathSegments[legacyIndex + 1] || 'home';
  } else {
    // Non-legacy path: /client/[view]
    currentView = pathSegments[1] || 'home';
  }

  // Check if we're in legacy mode (for banner display and navigation)
  const isLegacyRoute = legacyIndex !== -1;

  // Onboarding and modal state
  const {
    isFirstLogin,
    needsProfileCompletion,
    isLoading: onboardingLoading,
    skipProfileCompletion
  } = useClientOnboarding(user);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'welcome' | 'incomplete'>('welcome');
  const [modalShownThisSession, setModalShownThisSession] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    if (user?.id && !onboardingLoading) {
      // Check if modal was already shown this browser session
      const profileModalShown = sessionStorage.getItem('profileModalShown');
      const wasModalShown = profileModalShown === 'true';

      // Only block if modal was already shown in this session (component or browser session)
      if (modalShownThisSession || wasModalShown) {
        return;
      }

      // Determine which modal to show
      if (isFirstLogin) {
        // True first login - show welcome modal
        setModalType('welcome');
        setShowModal(true);
        setModalShownThisSession(true);
        sessionStorage.setItem('profileModalShown', 'true');
      } else if (needsProfileCompletion && !isFirstLogin) {
        // Subsequent login with incomplete profile - show nag modal
        setModalType('incomplete');
        setShowModal(true);
        setModalShownThisSession(true);
        sessionStorage.setItem('profileModalShown', 'true');
      }
    }
  }, [user?.id, onboardingLoading, isFirstLogin, needsProfileCompletion, modalShownThisSession]);

  // Determine the base path for navigation
  const basePath = isLegacyRoute ? '/client/legacy' : '/client';

  // ESC key handler to return to home from any view
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentView !== 'home' && currentView !== 'dashboard') {
        navigate(`${basePath}/home`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView, navigate, basePath]);

  const handleNavigate = (view: string) => {
    navigate(`${basePath}/${view}`);
  };

  const handleModalAccept = async () => {
    setShowModal(false);
    // Modal already marked as shown in useEffect, no need to set again

    // Mark welcome modal as seen if this was the welcome modal
    if (modalType === 'welcome') {
      try {
        await fetch(`/api/users/${user.id}/mark-welcome-seen`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch (error) {
        console.error('Failed to mark welcome modal as seen:', error);
      }
    }

    // Navigate to profile view after clearing flag
    setTimeout(() => {
      handleNavigate('profile');
    }, 100);
  };

  const handleModalClose = async () => {
    setShowModal(false);
    // Modal already marked as shown in useEffect, no need to set again

    // Mark welcome modal as seen if this was the welcome modal (same as accept handler)
    if (modalType === 'welcome') {
      try {
        await fetch(`/api/users/${user.id}/mark-welcome-seen`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch (error) {
        console.error('Failed to mark welcome modal as seen:', error);
      }
    }

    skipProfileCompletion();
    // Stay on dashboard
  };

  const handleDirectProfileNavigation = async () => {
    // Navigate directly to profile view (modal already marked as shown)
    handleNavigate('profile');
  };

  const renderContent = () => {
    switch (currentView) {
      case 'home':
      case 'dashboard': // Legacy route support
        return (
          <div className="dashboard-content">
            {needsProfileCompletion && (
              <div className="profile-incomplete-banner">
                <div className="banner-content">
                  <div className="banner-icon">⚠️</div>
                  <div className="banner-text">
                    <h3>Profile Incomplete</h3>
                    <p>Complete your profile to get started with Mission: Trimpossible</p>
                  </div>
                  <button
                    onClick={handleDirectProfileNavigation}
                    className="button button-primary"
                  >
                    Complete Profile
                  </button>
                </div>
              </div>
            )}

            <div className="dashboard-header">
              <h1>Home</h1>
            </div>

            <TileGrid onNavigate={handleNavigate} />
          </div>
        );
      case 'progress':
      case 'health-metrics': // Legacy route support
      case 'nutrition': // Legacy route support
      case 'analytics': // Legacy route support
        return (
          <div className="dashboard-content">
            <div className="dashboard-header">
              <h1>Progress</h1>
            </div>
            <p className="placeholder-text">Your progress tracking dashboard is coming soon...</p>
          </div>
        );
      case 'goals':
        return (
          <div className="dashboard-content">
            <GoalManagement readOnly={user?.self_managed === false} />
          </div>
        );
      case 'documents':
      case 'reports': // Legacy route support
        return (
          <div className="dashboard-content">
            <div className="dashboard-header">
              <h1>Documents</h1>
            </div>
            <p className="placeholder-text">Your documents and forms will appear here...</p>
          </div>
        );
      case 'settings':
      case 'integrations': // Legacy route support
        return (
          <div className="dashboard-content">
            <div className="dashboard-header">
              <h1>Settings</h1>
            </div>
            <ClientIntegrations />
          </div>
        );
      case 'profile':
        return <ProfilePage />;
      default:
        return (
          <div className="dashboard-content">
            <div className="dashboard-header">
              <h1>{currentView}</h1>
            </div>
            <p className="placeholder-text">This section is coming soon...</p>
          </div>
        );
    }
  };

  return (
    <ClientLayout currentView={currentView} onNavigate={handleNavigate}>
      {/* View Mode Banner - shows different style based on route */}
      {isLegacyRoute ? (
        <div className="legacy-banner legacy">
          <span className="view-badge">Legacy View</span>
          <span className="view-text">You're viewing the classic dashboard.</span>
          <Link to={`/client${currentView !== 'home' && currentView !== 'dashboard' ? `/${currentView}` : ''}`} className="switch-view-link">
            Switch to New View →
          </Link>
        </div>
      ) : (
        <div className="legacy-banner new-view">
          <span className="view-badge">New View</span>
          <span className="view-text">You're viewing the updated dashboard.</span>
          <Link to={`/client/legacy${currentView !== 'home' && currentView !== 'dashboard' ? `/${currentView}` : ''}`} className="switch-view-link">
            Switch to Legacy View →
          </Link>
        </div>
      )}

      <Routes>
        {/* New consolidated routes */}
        <Route path="/" element={renderContent()} />
        <Route path="/home" element={renderContent()} />
        <Route path="/progress" element={renderContent()} />
        <Route path="/goals" element={renderContent()} />
        <Route path="/documents" element={renderContent()} />
        <Route path="/profile" element={renderContent()} />
        <Route path="/settings" element={renderContent()} />
        {/* Legacy route support - redirect to new equivalents */}
        <Route path="/dashboard" element={renderContent()} />
        <Route path="/health-metrics" element={renderContent()} />
        <Route path="/nutrition" element={renderContent()} />
        <Route path="/analytics" element={renderContent()} />
        <Route path="/reports" element={renderContent()} />
        <Route path="/integrations" element={renderContent()} />
        {/* Catch-all */}
        <Route path="/*" element={renderContent()} />
      </Routes>

      {/* Welcome/Profile Incomplete Modal */}
      {showModal && user && (
        <WelcomeModal
          user={user}
          type={modalType}
          onAccept={handleModalAccept}
          onClose={handleModalClose}
        />
      )}
      <style>{`
        .legacy-banner {
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin: -24px -24px 24px -24px;
          flex-wrap: wrap;
        }

        .legacy-banner.legacy {
          background: linear-gradient(135deg, #d97706, #b45309);
        }

        .legacy-banner.new-view {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .view-badge {
          background: rgba(0, 0, 0, 0.3);
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .view-text {
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          flex: 1;
        }

        .switch-view-link {
          color: white;
          font-weight: 600;
          text-decoration: none;
          padding: 6px 16px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          transition: background 0.2s ease;
        }

        .switch-view-link:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .dashboard-content {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
          height: 100%;
        }

        .dashboard-header {
          margin-bottom: 32px;
        }

        .dashboard-header h1 {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .dashboard-header p {
          font-size: 16px;
          color: var(--text-secondary);
        }

        .profile-incomplete-banner {
          background: linear-gradient(135deg, var(--accent-orange), var(--accent-red));
          border-radius: var(--radius-large);
          padding: 2px;
          margin-bottom: 24px;
        }

        .banner-content {
          background: var(--bg-primary);
          border-radius: calc(var(--radius-large) - 2px);
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .banner-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .banner-text {
          flex: 1;
        }

        .banner-text h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 4px;
        }

        .banner-text p {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0;
        }

        .dashboard-welcome {
          text-align: center;
          padding: 60px 20px;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-green));
          border-radius: var(--radius-large);
          color: white;
          margin-bottom: 24px;
        }

        .dashboard-welcome h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 12px;
        }

        .dashboard-welcome p {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
        }

        .button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 12px 24px;
          border: none;
          border-radius: var(--radius-medium);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          white-space: nowrap;
        }

        .button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .button:active {
          transform: translateY(0);
        }

        .button-primary {
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-green));
          color: white;
          border: 2px solid transparent;
        }

        .button-primary:hover {
          background: linear-gradient(135deg, #3b82f6, #10b981);
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
          filter: brightness(1.1);
        }

        .dashboard-content h1 {
          color: var(--text-primary);
          margin-bottom: 16px;
        }
        .dashboard-content p {
          color: var(--text-secondary);
        }

        .placeholder-text {
          color: var(--text-tertiary);
          font-size: 16px;
          padding: 40px 20px;
          text-align: center;
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          border: 1px dashed var(--border-secondary);
        }

        @media (max-width: 768px) {
          .dashboard-header h1 {
            font-size: 28px;
          }

          .banner-content {
            flex-direction: column;
            text-align: center;
          }

          .button {
            width: 100%;
          }

          .legacy-banner {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }
        }
      `}</style>
    </ClientLayout>
  );
};

export default ClientDashboardLegacy;
